import axios from 'axios';
import applyCaseMiddleware from 'axios-case-converter';

import i18n from '../i18n';

import { DEFAULT_LANGUAGE_CODE } from '~common';
import { AUTH_API_ENDPOINT } from '~configs';
import { getAuthHeader, GetParams, PostParams } from '~services';

const axiosClient = applyCaseMiddleware(
  axios.create({
    baseURL: AUTH_API_ENDPOINT,
    // https://github.com/axios/axios/issues/5058#issuecomment-1272107602
    // Example: Params { a: ['b', 'c']}
    // From (by default - false) 'a[]=b&a[]=c'
    // To (by null) 'a=b&a=c'
    paramsSerializer: {
      indexes: null, // by default: false
    },
  }),
  {
    ignoreHeaders: true,
  },
);

export const getAuthRequest = async <T>({
  path,
  config,
  customHeader,
  params,
  ignoreAuthHeader,
  includeAppCheckHeader,
}: GetParams<T>) => {
  const authHeader = await getAuthHeader({
    ignoreAuthHeader,
    includeAppCheckHeader,
  });
  const response = await axiosClient.get(`/${path}`, {
    headers: {
      Accept: 'application/json',
      'Accept-Language': i18n.resolvedLanguage || DEFAULT_LANGUAGE_CODE,
      ...authHeader,
      ...customHeader,
    },
    params,
    ...config,
  });
  return response.data;
};

export const postAuthRequest = async <T>({
  path,
  config,
  customHeader,
  data,
  ignoreAuthHeader,
  includeAppCheckHeader,
}: PostParams<T>) => {
  const authHeader = await getAuthHeader({
    ignoreAuthHeader,
    includeAppCheckHeader,
  });
  return axiosClient.post(`/${path}`, data, {
    headers: {
      Accept: 'application/json',
      'Accept-Language': i18n.resolvedLanguage || DEFAULT_LANGUAGE_CODE,
      ...authHeader,
      ...customHeader,
    },
    ...config,
  });
};

export const putAuthRequest = async <T>({
  path,
  config,
  customHeader,
  data,
  ignoreAuthHeader,
  includeAppCheckHeader,
}: PostParams<T>) => {
  const authHeader = await getAuthHeader({
    ignoreAuthHeader,
    includeAppCheckHeader,
  });
  return axiosClient.put(`/${path}`, data, {
    headers: {
      Accept: 'application/json',
      'Accept-Language': i18n.resolvedLanguage || DEFAULT_LANGUAGE_CODE,
      ...authHeader,
      ...customHeader,
    },
    ...config,
  });
};

export const patchAuthRequest = async <T>({
  path,
  config,
  customHeader,
  data,
  ignoreAuthHeader,
  includeAppCheckHeader,
}: PostParams<T>) => {
  const authHeader = await getAuthHeader({
    ignoreAuthHeader,
    includeAppCheckHeader,
  });
  return axiosClient.patch(`/${path}`, data, {
    headers: {
      Accept: 'application/json',
      'Accept-Language': i18n.resolvedLanguage || DEFAULT_LANGUAGE_CODE,
      ...authHeader,
      ...customHeader,
    },
    ...config,
  });
};

export const delAuthRequest = async <T>({
  path,
  config,
  customHeader,
  ignoreAuthHeader,
  includeAppCheckHeader,
}: GetParams<T>) => {
  const authHeader = await getAuthHeader({
    ignoreAuthHeader,
    includeAppCheckHeader,
  });
  return axiosClient.delete(`/${path}`, {
    headers: {
      Accept: 'application/json',
      'Accept-Language': i18n.resolvedLanguage || DEFAULT_LANGUAGE_CODE,
      ...authHeader,
      ...customHeader,
    },
    ...config,
  });
};
